import {
    getGetServiceIntervalsUrl,
    getUpdateServiceIntervalsUrl
} from 'generatedTypes/asset/services/service-interval-controller';
import { useState } from 'react';

import useFetch from 'hooks/useFetch';
import useRetry from 'hooks/useRetry';

import useTranslation from './hooks/useTranslation';

import type {
    FetchServiceIntervals,
    ServiceIntervalBatchResponse,
    UpdateServiceIntervals,
    UseServiceIntervals
} from './types';

const useServiceIntervals = (): UseServiceIntervals => {
    const [isLoading, setIsLoading] = useState(false);
    const { authenticatedGet, authenticatedPut } = useFetch();
    const { withRetry } = useRetry();
    const { CONNECTION_ERROR, SAVE_FAILED } = useTranslation();

    const fetchServiceIntervals: FetchServiceIntervals = async ({ assetModelId }) => {
        try {
            setIsLoading(true);

            const url = getGetServiceIntervalsUrl({ assetModelId });

            return await withRetry(
                () => authenticatedGet<ServiceIntervalBatchResponse>(url),
                CONNECTION_ERROR,
                () => fetchServiceIntervals({ assetModelId })
            );
        } finally {
            setIsLoading(false);
        }
    };

    const updateServiceIntervals: UpdateServiceIntervals = async ({ assetModelId, intervals }) => {
        try {
            setIsLoading(true);

            const url = getUpdateServiceIntervalsUrl();

            return await withRetry(
                () =>
                    authenticatedPut<ServiceIntervalBatchResponse>(
                        url,
                        JSON.stringify({ assetModelId, intervals })
                    ),
                SAVE_FAILED,
                () => updateServiceIntervals({ assetModelId, intervals })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return {
        fetchServiceIntervals,
        isLoading,
        updateServiceIntervals
    };
};

export default useServiceIntervals;
